import React, { useState } from 'react'
import { t } from '../../utils/markets'
import {
    annualTiles,
    gasTiles,
    nonWallMountKey,
    nonWallMountLabel,
    repairTile,
    servicingTiles,
    drainFlushTiles,
} from '../../utils/market-aircon-booking'
import { BookingModeNote, PriceTiles, Toggle } from './ui'
import { formatMoney } from '../../utils/format-money'
import { useMarketBooking } from './MarketBooking'

const SERVICE_KEYS = {
    general: 'acGeneral',
    chemicalWash: 'acChemicalWash',
    chemicalOverhaul: 'acChemicalOverhaul',
}

/**
 * Service + unit-type + unit-count price picker for general servicing and the
 * two chemical services. `services` limits the tabs (a service page shows only
 * its own service; hubs show all three).
 */
export const ServicingPanel = ({ market, tables, services = ['general', 'chemicalWash', 'chemicalOverhaul'], defaultMount = 'Wall' }) => {
    const [service, setService] = useState(services[0])
    const [mount, setMount] = useState(defaultMount)
    const otherMount = nonWallMountKey(market)

    // The UAE lists ducted first: concealed ducted splits are the usual
    // apartment unit there. Malaysia's default is the wall-mounted split.
    const mountOptions = market.countryCd === 'ae'
        ? [{ value: otherMount, label: nonWallMountLabel(market) }, { value: 'Wall', label: t(market, 'mountWall') }]
        : [{ value: 'Wall', label: t(market, 'mountWall') }, { value: otherMount, label: nonWallMountLabel(market) }]

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                {services.length > 1 && (
                    <Toggle
                        label="Service"
                        options={services.map((s) => ({ value: s, label: t(market, SERVICE_KEYS[s]) }))}
                        value={service}
                        onChange={setService}
                    />
                )}
                <Toggle label="Unit type" options={mountOptions} value={mount} onChange={setMount} />
            </div>
            <PriceTiles countryCd={market.countryCd} tiles={servicingTiles(market, tables, service, mount)} />
            <BookingModeNote market={market} />
        </div>
    )
}

export const AnnualPanel = ({ market, tables }) => {
    const [times, setTimes] = useState(3)
    return (
        <div className="space-y-4">
            <Toggle
                label="Visits per year"
                options={[{ value: 3, label: '3 visits a year' }, { value: 4, label: '4 visits a year' }]}
                value={times}
                onChange={setTimes}
            />
            <PriceTiles countryCd={market.countryCd} tiles={annualTiles(market, tables, times)} note="Price per year, all visits included. Wall-mounted units." />
            <BookingModeNote market={market} />
        </div>
    )
}

export const GasPanel = ({ market, tables }) => {
    const [refrigerant, setRefrigerant] = useState(market.countryCd === 'my' ? 'R32' : 'R410A')
    const [half, setHalf] = useState(true)
    const refrigerants = market.countryCd === 'my' ? ['R32', 'R410A', 'R22'] : ['R410A', 'R32', 'R22']
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <Toggle label="Refrigerant" options={refrigerants.map((r) => ({ value: r, label: r }))} value={refrigerant} onChange={setRefrigerant} />
                <Toggle
                    label="Quantity"
                    options={[{ value: true, label: 'Top-up' }, { value: false, label: 'Full refill' }]}
                    value={half}
                    onChange={setHalf}
                />
            </div>
            <PriceTiles countryCd={market.countryCd} tiles={gasTiles(market, tables, refrigerant, half)} note="Priced per outdoor unit (compressor). The technician checks pressure first and tells you if a leak needs fixing before refilling." />
            <BookingModeNote market={market} />
        </div>
    )
}

export const RepairPanel = ({ market, tables, problems }) => {
    const { book } = useMarketBooking()
    const [problem, setProblem] = useState(problems[0])
    const tile = repairTile(market, tables, problem)
    return (
        <div className="space-y-4">
            <Toggle label="Problem" options={problems.map((p) => ({ value: p, label: p }))} value={problem} onChange={setProblem} />
            <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-orange-200 bg-orange-50 p-5">
                <div>
                    <p className="text-sm text-slate-600">Diagnosis visit</p>
                    <p className="text-2xl font-extrabold text-slate-900">{formatMoney(tile.price, market.countryCd)}</p>
                </div>
                <button
                    type="button"
                    onClick={() => book(tile)}
                    className="rounded-full bg-orange-500 px-5 py-2.5 font-bold text-white transition hover:bg-orange-600"
                >
                    Book a diagnosis
                </button>
                <p className="w-full text-xs text-slate-500">
                    The technician finds the fault and quotes the repair before starting. Parts are quoted separately.
                </p>
            </div>
            <BookingModeNote market={market} />
        </div>
    )
}

export const DrainFlushPanel = ({ market, tables }) => (
    <div className="space-y-4">
        <PriceTiles countryCd={market.countryCd} tiles={drainFlushTiles(market, tables)} />
        <BookingModeNote market={market} />
    </div>
)
